import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { X, MessageCircle, Clock, Send } from 'lucide-react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

export function ScheduledReportReminder() {
  const { isAuthenticated } = useAuth();
  const language = "ar";
  const [showReminder, setShowReminder] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const isArabic = language === 'ar';

  useEffect(() => {
    if (!isAuthenticated) return;

    // جلب الإعدادات
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/scheduled-reports`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error loading scheduled report settings:', error);
      }
    };

    loadSettings();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!settings?.daily_report_enabled || dismissed) return;

    const checkTime = () => {
      const now = new Date();
      const [hours, minutes] = settings.daily_report_time.split(':').map(Number);
      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      // التحقق مما إذا كان الوقت الحالي ضمن نطاق 15 دقيقة من وقت التذكير
      const scheduledMinutes = hours * 60 + minutes;
      const currentTotalMinutes = currentHours * 60 + currentMinutes;
      const diff = Math.abs(currentTotalMinutes - scheduledMinutes);

      // إظهار التذكير إذا كان ضمن 15 دقيقة من الوقت المحدد
      // والتحقق من عدم إرسال التقرير اليوم
      if (diff <= 15) {
        const lastSent = localStorage.getItem('last_daily_report_sent');
        const today = now.toDateString();
        
        if (lastSent !== today) {
          loadReportData();
          setShowReminder(true);
        }
      }
    };

    // التحقق كل دقيقة
    const interval = setInterval(checkTime, 60000);
    checkTime(); // التحقق فوراً

    return () => clearInterval(interval);
  }, [settings, dismissed]);

  const loadReportData = async () => {
    try {
      const response = await axios.get(`${API}/analytics/financial-summary`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error loading report data:', error);
    }
  };

  const sendReport = () => {
    if (!reportData) return;

    const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
    const now = new Date();
    const today = reportData.today || {};
    const debts = reportData.debts || {};

    let message = isArabic 
      ? `📊 *التقرير اليومي*\n`
      : `📊 *Daily Report*\n`;
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += `📅 ${now.toLocaleDateString(isArabic ? "ar-IQ" : "en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}\n`;
    message += `━━━━━━━━━━━━━━━\n\n`;
    
    if (settings?.include_sales) {
      message += isArabic 
        ? `💰 *المبيعات:* ${formatNum(today.sales || 0)} د.ع\n`
        : `💰 *Sales:* ${formatNum(today.sales || 0)} د.ع\n`;
      message += isArabic
        ? `🔢 *المعاملات:* ${today.transactions || 0}\n`
        : `🔢 *Transactions:* ${today.transactions || 0}\n`;
    }
    
    if (settings?.include_profit) {
      message += isArabic
        ? `📈 *الربح:* ${formatNum(today.profit || 0)} د.ع\n`
        : `📈 *Profit:* ${formatNum(today.profit || 0)} د.ع\n`;
    }
    
    if (settings?.include_debts) {
      message += `\n`;
      message += isArabic
        ? `💳 *الديون المستحقة:* ${formatNum(debts.receivable || 0)} د.ع\n`
        : `💳 *Receivables:* ${formatNum(debts.receivable || 0)} د.ع\n`;
    }
    
    message += `━━━━━━━━━━━━━━━\n`;
    message += `🕐 ${now.toLocaleTimeString(isArabic ? "ar-IQ" : "en-US")}\n`;
    message += isArabic ? `✅ تقرير من نازاريو حساباتي` : `✅ Report from Nazario-Hesabaty`;

    const encodedMessage = encodeURIComponent(message);
    
    let phone = settings?.whatsapp_recipient?.replace(/\D/g, '');
    if (phone) {
      if (phone.startsWith('07')) {
        phone = '964' + phone.substring(1);
      } else if (!phone.startsWith('964')) {
        phone = '964' + phone;
      }
      window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
    } else {
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    }

    // تسجيل أنه تم إرسال التقرير اليوم
    localStorage.setItem('last_daily_report_sent', now.toDateString());
    setShowReminder(false);
  };

  const dismissReminder = () => {
    setShowReminder(false);
    setDismissed(true);
  };

  if (!showReminder) return null;

  const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
  const today = reportData?.today || {};

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 z-50 animate-fade-in-up">
      <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl shadow-2xl p-5 border border-green-400/30">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-white/20">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold">
                {isArabic ? 'وقت التقرير اليومي!' : 'Daily Report Time!'}
              </h3>
              <p className="text-green-100 text-sm">
                {isArabic ? 'هل تريد إرسال ملخص اليوم؟' : 'Send today\'s summary?'}
              </p>
            </div>
          </div>
          <button 
            onClick={dismissReminder}
            className="text-green-100 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {reportData && (
          <div className="bg-white/10 rounded-xl p-3 mb-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-green-100">{isArabic ? 'المبيعات:' : 'Sales:'}</span>
              <span className="text-white font-bold">{formatNum(today.sales || 0)} د.ع</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-green-100">{isArabic ? 'الربح:' : 'Profit:'}</span>
              <span className="text-white font-bold">{formatNum(today.profit || 0)} د.ع</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-green-100">{isArabic ? 'المعاملات:' : 'Transactions:'}</span>
              <span className="text-white font-bold">{today.transactions || 0}</span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={sendReport}
            className="flex-1 bg-white text-green-700 hover:bg-green-50 font-bold"
          >
            <Send className="w-4 h-4 me-2" />
            {isArabic ? 'إرسال الآن' : 'Send Now'}
          </Button>
          <Button
            onClick={dismissReminder}
            variant="outline"
            className="border-white/50 text-white hover:bg-white/10"
          >
            {isArabic ? 'لاحقاً' : 'Later'}
          </Button>
        </div>
      </div>
    </div>
  );
}
